/**
 * Spec 217 PR4.b — Spanish translation table.
 *
 * Keys are namespaced by surface (sidebar.*, settings.*, common.*)
 * to keep the file readable and the lookups predictable. Values are
 * plain strings; interpolation uses the i18next default `{{var}}`
 * syntax when needed.
 *
 * The default locale is Spanish (Cuadrilla's primary user base).
 * English is provided as the secondary locale via Settings, and the
 * detector picks the browser language at first run when there's no
 * persisted preference.
 *
 * NOT every visible string is migrated yet — this PR adds the
 * scaffolding + the highest-traffic chrome (sidebar nav + Settings
 * titles + common buttons). Other surfaces stay hardcoded for now;
 * a follow-up sweep will pull them in incrementally.
 */
export const es = {
  common: {
    save: 'Guardar',
    saved: 'Guardado',
    cancel: 'Cancelar',
    back: 'Volver',
    loading: 'Cargando…',
  },
  sidebar: {
    works: 'Trabajos',
    newWork: 'Nuevo trabajo',
    stats: 'Estadísticas',
    clients: 'Clientes',
    broadcasts: 'Avisos',
    settings: 'Ajustes',
  },
  settings: {
    title: 'Ajustes',
    profile: 'Perfil',
    profileName: 'Nombre',
    profileRole: 'Rol',
    profileCompanyId: 'ID empresa',
    profilePaired: 'Emparejado',
    team: 'Equipo',
    teamName: 'Nombre del equipo',
    companyName: 'Nombre de la empresa',
    members: 'Miembros',
    youBadge: 'TÚ',
    membersEmpty: 'Aún no hay miembros sincronizados.',
    memberJoinedAt: 'Miembro desde {{date}}',
    teamReadonlyHint: 'solo el propietario puede cambiarlo',
    teamErrorEmpty: 'El nombre no puede estar vacío.',
    teamErrorTooLong: 'El nombre debe tener 80 caracteres o menos.',
    appearance: 'Apariencia',
    appearanceTheme: 'Tema',
    appearanceThemeAuto: 'Automático',
    appearanceThemeLight: 'Claro',
    appearanceThemeDark: 'Oscuro',
    appearanceFontSize: 'Tamaño de texto',
    appearanceFontSizeSmall: 'Compacto',
    appearanceFontSizeMedium: 'Normal',
    appearanceFontSizeLarge: 'Grande',
    language: 'Idioma',
    languageEs: 'Español',
    languageEn: 'English',
    logout: 'Cerrar sesión',
    brandingTitle: 'Identidad de empresa',
    brandingDescription: 'Estos datos aparecen en cada PDF que generes desde Cuadrilla.',
    brandingCompanyName: 'Razón social',
    brandingTaxId: 'NIF / CIF',
    brandingPhone: 'Teléfono',
    brandingEmail: 'Email',
    brandingEmailInvalid: 'Revisa el formato del email.',
    brandingWeb: 'Sitio web',
    brandingLogoMobileOnly: 'Logo: edítalo desde la app móvil',
    brandingUploadLogo: 'Subir logo',
    brandingChangeLogo: 'Cambiar logo',
    brandingRemoveLogo: 'Eliminar logo',
    brandingLogoTooLarge: 'Logo demasiado grande (máx 256 KB).',
    brandingLogoHint: 'PNG, JPG o SVG, máx 256 KB.',
    brandingSaved: 'Guardado',
  },
  pair: {
    brandName: 'Cuadrilla web',
    heroTitle: 'Tus trabajos, en cualquier pantalla',
    heroTagline: 'Empareja este navegador con tu móvil para escribir notas, repasar fotos y exportar PDFs sin perder la privacidad de extremo a extremo.',
    bullet1Title: 'Cifrado de extremo a extremo.',
    bullet1Body: 'El servidor solo ve ruido — ni nosotros podemos leer tus trabajos.',
    bullet2Title: 'El móvil sigue siendo el control.',
    bullet2Body: 'Cámara, firma y voz se quedan en el dispositivo principal.',
    bullet3Title: 'macOS, Windows y Linux.',
    bullet3Body: 'Sin instalar nada — un navegador moderno es suficiente.',
    mainTitle: 'Empareja con tu móvil',
    step1: 'Abre Cuadrilla en tu móvil.',
    step2: 'Entra en <1>Ajustes → «Cuadrilla web»</1>.',
    step3: 'Escanea este código.',
    generating: 'Generando QR seguro…',
    expired: 'QR caducado, generando otro…',
    qrAlt: 'Código QR para emparejar el móvil',
    codeLabel: 'Código de verificación',
    codeHint: 'Comprueba que coincide con el que ves en el móvil antes de confirmar.',
    paired: 'Emparejado. Cargando tus trabajos…',
    error: 'No se pudo emparejar.',
    retry: 'Reintentar',
    caveatTitle: 'El navegador no impide capturas de pantalla.',
    caveatBody: 'A diferencia de la app móvil, las extensiones, las herramientas del sistema y las grabaciones del escritorio pueden ver esta pestaña. Cierra la sesión cuando termines y evita usar Cuadrilla web en ordenadores compartidos.',
  },
};

export type ResourceShape = typeof es;
